import React, { useEffect, useState } from 'react';
import { fetchTodos, createTodo, updateTodo, deleteTodo } from './api/todoApi';
import { Todo, CreateTodoDto, UpdateTodoDto } from './types/todo';
import TodoForm from './components/TodoForm';
import TodoItem from './components/TodoItem';
import { getOrGenerateGUID, setGUID } from './utils/guid';
import useStoicQuote from './hooks/useStoicQuote';
import { FaPlus, FaMinus } from 'react-icons/fa'; // Import icons
import Modal from 'react-modal';

const App: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copyText, setCopyText] = useState('Copy to Clipboard');
  const [error, setError] = useState<string | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const { quote } = useStoicQuote();

  useEffect(() => {
    updateGuidFromURL()
    const loadTodos = async () => {
      const fetchedTodos = await fetchTodos();
      setTodos(fetchedTodos);
    };
    loadTodos();
  }, []);

  const updateGuidFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlGuid = urlParams.get('u');
    if (urlGuid && urlGuid !== getOrGenerateGUID()) {
      setGUID(urlGuid); // Update the user GUID from the URL
    } else {
      getOrGenerateGUID();
    }
  };

  const handleCreate = async (data: CreateTodoDto) => {
    try {
      const updatedTodos = await createTodo(data);
      setTodos(updatedTodos);
      setError(null)
    } catch (error: any) {
      setError(error.response.data.message);
      setIsFormVisible(false)
    }
  };

  const handleUpdate = async (id: string, data: UpdateTodoDto) => {
    const updatedTodos = await updateTodo(id, { ...data });
    setTodos(updatedTodos);
  };

  const handleDelete = async (id: string) => {
    const updatedTodos = await deleteTodo(id);
    setTodos(updatedTodos);
    setError(null)
  };

  const handleCopyToClipboard = () => {
    const link = `${window.location.origin}/?u=${getOrGenerateGUID()}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopyText('Copied!');
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 flex flex-col">
      <header className="text-center mt-8 mb-8">
        <img
          src="/logo.png"
          alt="Application Logo"
          className="w-24 h-24 mx-auto rounded-full border border-gray-700"
        />
        <h1 className="text-4xl font-extrabold mt-4 text-gray-100">Zen List</h1>
        {quote && (
          <div className="mt-6 text-gray-400 italic text-lg max-w-xl mx-auto">
            <p>"{quote.text}"</p>
            <p className="mt-2 text-right">— {quote.author}</p>
          </div>
        )}
      </header>
      <main className="w-full max-w-2xl px-4 flex-grow self-center">
      {error && (
          <div className="mb-4 bg-red-100 text-red-800 px-4 py-2 rounded">
            {error}
          </div>
        )}
        {/* Toggle Form Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setIsFormVisible((prev) => !prev)}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-semibold transition duration-200"
          >
            {isFormVisible ? <FaMinus size={20}/> : <FaPlus size={20} />}
          </button>
        </div>
        {/* Todo Form */}
        {isFormVisible && <TodoForm onCreate={handleCreate} />}
        {/* Todo List */}
        <div className={`mt-8 ${isFormVisible ? 'max-h-64' : 'max-h-96'} overflow-y-auto space-y-4 pr-2`}>
          {todos.map((todo) => (
            <TodoItem
              key={todo.id}
              todo={todo}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </main>
      <footer className="w-full flex bg-gray-800 text-gray-400 text-center py-4">
        <div className="ml-auto">
          <p className="italic">"All tasks are fleeting; they are deleted at the start of each day."</p>
          <a href='https://rxbsxn.com' target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">@rxbsxn</a>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="ml-auto mr-8 bg-blue-600 hover:bg-blue-700 text-white px-4 rounded font-semibold transition duration-200">Synchronize</button>
      </footer>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center"
        className="bg-gray-800 text-gray-200 p-6 rounded max-w-md w-full text-center"
      >
        <h1 className="text-xl font-bold text-gray-400">Synchronize your list</h1>
        <h3 className="text-ml mb-4 ">Bring your tasks to different devices</h3>
        <p className="mb-4 rounded px-2 py-2 bg-gray-700">
          {window.location.origin}/?u={getOrGenerateGUID()}
        </p>
        <div className='flex justify-between'>
          <button
            onClick={handleCopyToClipboard}
            className="self-end bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition"
          >
            {copyText}
          </button>
          <button
            onClick={() => setIsModalOpen(false)}
            className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default App;
